/**
 * Scope Check Utility
 *
 * Validates record scope for write operations (PATCH/DELETE/submit/cancel).
 * Centralizes branch scope + record scope + sharing checks to avoid duplication
 * in every route handler.
 */

import { isRecordInBranchScope } from "./branch-scope";
import type { BranchScopeConfig } from "./branch-scope";
import type { ScopeType } from "../types/module";

/**
 * Validate record scope for write operations (PATCH/DELETE/submit/cancel).
 * Returns error message if scope validation fails, or null if OK.
 */
export function validateRecordScope(
  record: Record<string, any>,
  options: {
    branchScope?: BranchScopeConfig;
    userBranchIds: string[] | null;
    scopeType: ScopeType;
    userId: string;
    assignedField?: string;
    sharePermission?: { canRead: boolean; canWrite: boolean } | null;
  }
): string | null {
  // 1. Branch scope check
  if (options.branchScope && options.userBranchIds !== null) {
    if (
      !isRecordInBranchScope(record, options.branchScope, {
        userBranchIds: options.userBranchIds,
        activeBranchId: null,
      })
    ) {
      return "ไม่มีสิทธิ์เข้าถึงข้อมูลสาขานี้";
    }
  }

  // 2. Record scope check (with sharing override)
  if (options.scopeType !== "all") {
    const isOwn = record.createdBy === options.userId;
    const isAssigned = options.assignedField && record[options.assignedField] === options.userId;

    let inScope = false;

    if (options.scopeType === "own") {
      inScope = isOwn;
    } else if (options.scopeType === "own_and_assigned") {
      inScope = isOwn || !!isAssigned;
    } else if (options.scopeType === "assigned_only") {
      inScope = options.assignedField ? !!isAssigned : isOwn; // fallback to own if no assignedField
    }

    if (!inScope) {
      if (!options.sharePermission?.canWrite) {
        return "สามารถแก้ไขได้เฉพาะข้อมูลที่อยู่ในขอบเขตสิทธิ์ของคุณ";
      }
    }
  }

  return null; // OK
}
